/** Herbruikbare head-metadata voor de Engelse (en) pagina's onder /en/. */
import { SITE, hreflangLinks } from "@/lib/i18n-urls";
import { clampTitle, withMetaCta } from "@/lib/meta-cta";

const DEFAULT_OG_IMAGE = `${SITE}/og/taxiritje-brand.jpg`;

/** Typen die de pagina zelf al beschrijven; dan geen extra WebPage toevoegen. */
function describesPage(type: unknown): boolean {
  const types = Array.isArray(type) ? type : [type];
  return types.some(
    (t) =>
      typeof t === "string" &&
      (t.endsWith("Page") || t === "Blog" || t === "Article" || t === "BlogPosting"),
  );
}

function webPageJsonLd(url: string, title: string, description: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: "en",
    isPartOf: { "@id": `${SITE}/#website` },
    publisher: { "@id": `${SITE}/#organization` },
  };
}

export function enPageHead({
  path,
  title,
  description,
  jsonLd,
  image,
  type = "website",
}: {
  /** Pad inclusief /en, bijvoorbeeld "/en/airport-taxi". */
  path: string;
  title: string;
  description: string;
  jsonLd?: unknown;
  image?: string;
  type?: string;
}) {
  const url = `${SITE}${path}`;
  title = clampTitle(title);
  const metaDescription = withMetaCta(description, "en");
  const ogImage = image ?? DEFAULT_OG_IMAGE;

  const provided = jsonLd === undefined ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  const hasPageSchema = provided.some(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      describesPage((item as { "@type"?: unknown })["@type"]),
  );
  const schemas = hasPageSchema ? provided : [webPageJsonLd(url, title, description), ...provided];

  return {
    meta: [
      { title },
      { name: "description", content: metaDescription },
      { property: "og:title", content: title },
      { property: "og:description", content: metaDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: type },
      { property: "og:site_name", content: "Taxiritje.nl" },
      { property: "og:locale", content: "en_GB" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: url }, ...hreflangLinks(path)],
    scripts: schemas.map((data) => ({
      type: "application/ld+json",
      children: JSON.stringify(data),
    })),
  };
}
